import { ArrowDownTrayIcon, CheckCircleIcon, EyeIcon, PaperClipIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@heroui/react";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { AttachmentDto } from "../../../../types/types";
import { formatFileSize, getFileIcon, validateAndFilterFiles } from "../../../../utils/utilMethods";
import { VIDEO_UPLOAD_ENABLED } from "../../../../config/featureFlags";

interface FileAttachmentProps {
    selectedFiles: File[];
    setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>;
    existingAttachments: AttachmentDto[];
    attachmentsToRemove: string[];
    setAttachmentsToRemove: React.Dispatch<React.SetStateAction<string[]>>;
    uploadingFiles: boolean;
    isDragging: boolean;
    setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
    dragCounter: number;
    setDragCounter: React.Dispatch<React.SetStateAction<number>>;
}

export const FileAttachmentHandlers: React.FC<FileAttachmentProps> = ({
    selectedFiles,
    setSelectedFiles,
    existingAttachments,
    attachmentsToRemove,
    setAttachmentsToRemove,
    uploadingFiles,
    isDragging,
    setIsDragging,
    dragCounter,
    setDragCounter,
}) => {
    const { t } = useTranslation('dealModal');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (files: File[]) => {
        setSelectedFiles(files);
    };

    const removeFile = (index: number) => {
        const newFiles = selectedFiles.filter((_, i) => i !== index);
        setSelectedFiles(newFiles);
    };

    const removeExistingAttachment = (attachmentUrl: string | undefined) => {
        if (attachmentUrl) {
            setAttachmentsToRemove((prev) => [...prev, attachmentUrl]);
        }
    };

    const restoreExistingAttachment = (attachmentUrl: string | undefined) => {
        if (attachmentUrl) {
            setAttachmentsToRemove((prev) =>
                prev.filter((url) => url !== attachmentUrl)
            );
        }
    };

    const isVideoFile = (file: File) => file.type.startsWith("video/");

    const clearAllAttachments = () => {
        setSelectedFiles([]);
        setAttachmentsToRemove(
            existingAttachments.map((att) => att.fileUrl).filter(Boolean) as string[]
        );
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        const allowedFiles = VIDEO_UPLOAD_ENABLED
            ? files
            : files.filter((file) => !isVideoFile(file));

        if (allowedFiles.length > 0) {
            const validFiles = validateAndFilterFiles([...selectedFiles, ...allowedFiles]);
            handleFileSelect(validFiles);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragCounter((prev) => prev + 1);
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragCounter((prev) => prev - 1);
        if (dragCounter <= 1) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        setDragCounter(0);

        const files = Array.from(e.dataTransfer.files);
        const allowedFiles = VIDEO_UPLOAD_ENABLED
            ? files
            : files.filter((file) => !isVideoFile(file));

        if (allowedFiles.length > 0) {
            const validFiles = validateAndFilterFiles([...selectedFiles, ...allowedFiles]);
            handleFileSelect(validFiles);
        }
    };

    const renderFilePreview = (file: File, index: number) => {
        const extension = file.name.split(".").pop()?.toLowerCase();
        const previewUrl = URL.createObjectURL(file);

        if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "")) {
            return (
                <div className="relative inline-block">
                    <img
                        src={previewUrl}
                        alt={file.name}
                        className="w-16 h-16 object-cover rounded-lg max-w-[200px] max-h-[200px]"
                    />
                    <button
                        onClick={() => removeFile(index)}
                        className="absolute top-1 right-1 bg-gray-800 bg-opacity-50 rounded-full p-1"
                        disabled={uploadingFiles}
                    >
                        <XMarkIcon className="w-2 h-2 text-white" />
                    </button>
                </div>
            );
        } else {
            return (
                <div className="relative inline-flex items-center gap-2 bg-gray-100 rounded-lg p-2 border mr-2 mb-2">
                    <div className="text-gray-600">{getFileIcon(file.name)}</div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-700 truncate max-w-[120px]">
                            {file.name}
                        </p>
                        <p className="text-[10px] text-gray-500">
                            {formatFileSize(file.size)}
                        </p>
                    </div>
                    <button
                        onClick={() => removeFile(index)}
                        className="text-gray-500 hover:text-gray-700 p-1"
                        disabled={uploadingFiles}
                    >
                        <XMarkIcon className="w-2 h-2" />
                    </button>
                </div>
            );
        }
    };

    const renderExistingAttachment = (
        attachment: AttachmentDto,
        index: number
    ) => {
        const isMarkedForRemoval =
            attachment.fileUrl && attachmentsToRemove.includes(attachment.fileUrl);
        const extension = attachment.fileName.split(".").pop()?.toLowerCase();
        const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(
            extension || ""
        );

        if (!attachment.fileUrl) {
            return null;
        }

        return (
            <div
                key={`existing-${index}`}
                className={`relative ${isMarkedForRemoval ? "opacity-50" : ""}`}
            >
                {isImage ? (
                    <div className="relative inline-block">
                        <img
                            src={attachment.fileUrl}
                            alt={attachment.fileName}
                            className="w-16 h-16 object-cover rounded-lg max-w-[200px] max-h-[200px]"
                        />
                        <div className="absolute top-1 right-1 flex gap-1">
                            <button
                                onClick={() => window.open(attachment.fileUrl, "_blank")}
                                className="bg-blue-500 bg-opacity-75 rounded-full p-1"
                                title={t("viewFile")}
                            >
                                <EyeIcon className="w-2 h-2 text-white" />
                            </button>
                            {isMarkedForRemoval ? (
                                <button
                                    onClick={() => restoreExistingAttachment(attachment.fileUrl)}
                                    className="bg-green-500 bg-opacity-75 rounded-full p-1"
                                    title={t("restoreFile")}
                                >
                                    <CheckCircleIcon className="w-2 h-2 text-white" />
                                </button>
                            ) : (
                                <button
                                    onClick={() => removeExistingAttachment(attachment.fileUrl)}
                                    className="bg-red-500 bg-opacity-75 rounded-full p-1"
                                    title={t("removeFile")}
                                    disabled={uploadingFiles}
                                >
                                    <XMarkIcon className="w-2 h-2 text-white" />
                                </button>
                            )}
                        </div>
                        {isMarkedForRemoval && (
                            <div className="absolute inset-0 bg-red-500 bg-opacity-20 rounded-lg flex items-center justify-center">
                                <span className="text-red-600 font-bold text-xs">{t("removed")}</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="relative inline-flex items-center gap-2 bg-gray-100 rounded-lg p-2 border mr-2 mb-2">
                        <div className="text-gray-600">
                            {getFileIcon(attachment.fileName)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-700 truncate max-w-[120px]">
                                {attachment.fileName}
                            </p>
                            <p className="text-[10px] text-gray-500">
                                {formatFileSize(attachment.fileSize)}
                            </p>
                        </div>
                        <div className="flex gap-1">
                            <button
                                onClick={() => window.open(attachment.fileUrl, "_blank")}
                                className="text-blue-500 hover:text-blue-700 p-1"
                                title={t("viewFile")}
                            >
                                <EyeIcon className="w-3 h-3" />
                            </button>
                            {isMarkedForRemoval ? (
                                <button
                                    onClick={() => restoreExistingAttachment(attachment.fileUrl)}
                                    className="text-green-500 hover:text-green-700 p-1"
                                    title={t("restoreFile")}
                                >
                                    <CheckCircleIcon className="w-3 h-3" />
                                </button>
                            ) : (
                                <button
                                    onClick={() => removeExistingAttachment(attachment.fileUrl)}
                                    className="text-red-500 hover:text-red-700 p-1"
                                    title={t("removeFile")}
                                    disabled={uploadingFiles}
                                >
                                    <XMarkIcon className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                        {isMarkedForRemoval && (
                            <div className="absolute inset-0 bg-red-500 bg-opacity-20 rounded-lg flex items-center justify-center">
                                <span className="text-red-600 font-bold text-[8px]">
                                    {t("removed")}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderSelectedFiles = () => {
        const hasExistingAttachments = existingAttachments.length > 0;
        const hasNewFiles = selectedFiles.length > 0;

        if (!hasExistingAttachments && !hasNewFiles) return null;

        return (
            <div className="space-y-4">
                {hasExistingAttachments && (
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-gray-700">
                                {t("existingAttachments")}
                            </h4>
                            <span className="text-xs text-gray-500">
                                {t("attachmentsRemaining", {
                                    remaining: existingAttachments.length - attachmentsToRemove.length,
                                    total: existingAttachments.length,
                                })}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {existingAttachments.map((attachment, index) =>
                                renderExistingAttachment(attachment, index)
                            )}
                        </div>
                    </div>
                )}

                {hasNewFiles && (
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-gray-700">
                                {t("newAttachments")}
                            </h4>
                            <span className="text-xs text-gray-500">
                                {t("filesCount", { count: selectedFiles.length })}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {selectedFiles.map((file, index) => (
                                <div key={`new-${index}`}>{renderFilePreview(file, index)}</div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div
            className={`border-2 ${isDragging ? "border-dashed border-blue-500 bg-blue-50" : "border-gray-200"
                } rounded-lg p-4 relative`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <input
                type="file"
                ref={fileInputRef}
                multiple
                onChange={handleFileInputChange}
                className="hidden"
                accept={
                    VIDEO_UPLOAD_ENABLED
                        ? "image/*,video/*,.pdf,.doc,.docx"
                        : "image/*,.pdf,.doc,.docx"
                }
            />
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <PaperClipIcon className="w-5 h-5 text-gray-500" />
                    <span className="font-semibold text-gray-800">{t("attachments")}</span>
                </div>
                <div className="flex gap-2">
                    {(existingAttachments.length > 0 || selectedFiles.length > 0) && (
                        <Button
                            variant="light"
                            size="sm"
                            onPress={clearAllAttachments}
                            className="text-red-600 hover:text-red-800"
                            isDisabled={uploadingFiles}
                        >
                            {t("clearAll")}
                        </Button>
                    )}
                </div>
            </div>
            {existingAttachments.length > 0 || selectedFiles.length > 0 ? (
                <div className="mb-4">{renderSelectedFiles()}</div>
            ) : null}
            <div
                className="flex items-center justify-center h-24 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={openFileDialog}
            >
                <div className="text-center">
                    <ArrowDownTrayIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                        {isDragging ? t("dropFilesHere") : t("clickOrDragFiles")}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        {VIDEO_UPLOAD_ENABLED ? t("fileLimits") : t("fileLimitsWithoutVideo")}
                    </p>
                </div>
            </div>
        </div>
    );
};
